import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from '../../i18n';
import { Get, Send, Remove, Update } from '../../services/service.api';
import { toast } from 'react-toastify';
import { Plus, Trash2, X, Building2, Check, Rocket, Users } from 'lucide-react';
import ConfirmModal from '../../components/component.confirmModal';

const PLAN_LIMITS = { free: 2, pro: 10 };

function CompanyModal({ company, onClose, onSave, t }) {
  const [form, setForm] = useState({
    libelle: company?.libelle || '',
    capacite: company?.capacite || '',
    pays: company?.pays || '',
    ville: company?.ville || '',
    description: company?.description || '',
    domaine: company?.domaine || '',
  });

  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.libelle) return toast.error('Le libellé est obligatoire');
    setSaving(true);
    await onSave(form);
    setSaving(false);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" style={{ maxWidth: 580 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">{company ? 'Modifier l\'entreprise' : 'Nouvelle entreprise'}</h2>
          <button className="modal-close" onClick={onClose}><X size={16} /></button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-group">
              <label className="form-label">{t.company.libelle} <span className="required">*</span></label>
              <input className="form-control" value={form.libelle}
                onChange={e => setForm({ ...form, libelle: e.target.value })} placeholder="Nom de l'entreprise" />
            </div>
            <div className="grid-2">
              <div className="form-group">
                <label className="form-label">{t.company.pays}</label>
                <input className="form-control" value={form.pays}
                  onChange={e => setForm({ ...form, pays: e.target.value })} placeholder="Burkina Faso" />
              </div>
              <div className="form-group">
                <label className="form-label">{t.company.ville}</label>
                <input className="form-control" value={form.ville}
                  onChange={e => setForm({ ...form, ville: e.target.value })} placeholder="Ouagadougou" />
              </div>
            </div>
            <div className="grid-2">
              <div className="form-group">
                <label className="form-label">{t.company.capacite}</label>
                <input className="form-control" value={form.capacite}
                  onChange={e => setForm({ ...form, capacite: e.target.value })} placeholder="Capacité" />
              </div>
              <div className="form-group">
                <label className="form-label">{t.company.domaine}</label>
                <input className="form-control" value={form.domaine}
                  onChange={e => setForm({ ...form, domaine: e.target.value })} placeholder="Commerce, Agriculture..." />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">{t.company.description}</label>
              <textarea className="form-control" value={form.description} rows={3}
                onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Description de l'entreprise..." />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-outline" onClick={onClose} disabled={saving}>{t.common.cancel}</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Enregistrement...' : t.common.save}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function Companies() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const token = useSelector(s => s.userReducer.token);
  const user = useSelector(s => s.userReducer.user);
  const activeCompany = useSelector(s => s.userReducer.activeCompany);
  const isInvestor = user?.role === 'investisseur';
  const userPlan = user?.plan || 'free';
  const planLimit = PLAN_LIMITS[userPlan] || 2;

  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editCompany, setEditCompany] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  const fetchCompanies = useCallback(async () => {
    setLoading(true);
    try {
      let res = await Get('/companies/mine', token);
      if (!Array.isArray(res) || res.length === 0) {
        const all = await Get('/companies', token);
        if (Array.isArray(all)) res = all;
      }
      if (Array.isArray(res)) {
        setCompanies(res);
        // Auto-activate first company if none selected
        if (!activeCompany && res.length > 0) {
          dispatch({ type: 'set-company', value: res[0] });
        }
      }
    } finally { setLoading(false); }
  }, [token, activeCompany, dispatch]);

  useEffect(() => { fetchCompanies(); }, [fetchCompanies]);

  const handleSwitch = (company) => {
    dispatch({ type: 'set-company', value: company });
    toast.success(`🏢 Activé : ${company.libelle}`, { autoClose: 2000 });
    window.dispatchEvent(new Event('company-switched'));
  };

  const handleSave = async (form) => {
    try {
      let res;
      if (editCompany) {
        res = await Update(`/companies/${editCompany.id}`, form, token);
      } else {
        res = await Send('/companies', form, true, token);
      }
      if (res?.error) return toast.error(res.error);
      toast.success(editCompany ? 'Entreprise modifiée' : 'Entreprise créée ✓');
      setShowModal(false); setEditCompany(null);
      window.dispatchEvent(new Event('company-created'));
      fetchCompanies();
    } catch { toast.error('Erreur'); }
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    try {
      await Remove(`/companies/${deleteId}`, token);
      toast.success('Entreprise supprimée');
      if (activeCompany?.id === deleteId) dispatch({ type: 'set-company', value: null });
      setDeleteId(null);
      fetchCompanies();
    } catch { toast.error('Erreur'); }
  };

  const canCreate = companies.length < planLimit;

  return (
    <div className="page-content page-enter">
      <div className="page-header">
        <div>
          <h1>{t.nav.companies}</h1>
          <p style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {companies.length}/{planLimit} entreprise(s)
            <span style={{
              padding: '2px 8px', borderRadius: 12, fontSize: '0.72rem', fontWeight: 700,
              background: userPlan === 'pro' ? 'rgba(20,184,166,0.15)' : 'rgba(245,158,11,0.15)',
              color: userPlan === 'pro' ? 'var(--primary)' : '#f59e0b',
              border: `1px solid ${userPlan === 'pro' ? 'var(--primary)' : '#f59e0b'}`,
            }}>
              {userPlan === 'pro' ? '⭐ Pro' : '🆓 Free'}
            </span>
          </p>
        </div>
        {isInvestor && (
          <button
            className="btn btn-primary"
            onClick={() => {
              if (!canCreate) {
                toast.warn(`Limite atteinte (${planLimit} max). 🚀 Pro (10 entreprises, 15€/an) — bientôt disponible !`);
                return;
              }
              setEditCompany(null); setShowModal(true);
            }}
          >
            <Plus size={16} /> Nouvelle entreprise
          </button>
        )}
      </div>

      {/* Plan upgrade banner */}
      {userPlan === 'free' && (
        <div style={{
          background: 'linear-gradient(135deg, rgba(245,158,11,0.1), rgba(245,158,11,0.05))',
          border: '1px solid rgba(245,158,11,0.3)',
          borderRadius: 'var(--radius)', padding: '14px 18px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 12, marginBottom: 24, flexWrap: 'wrap',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Rocket size={18} color="#f59e0b" />
            <div>
              <div style={{ fontWeight: 700, fontSize: '0.9rem', color: 'var(--text-primary)' }}>
                SavInvest Pro — Bientôt disponible 🚀
              </div>
              <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                Gérez jusqu'à <strong>10 entreprises</strong> pour seulement <strong>15€/an</strong>
              </div>
            </div>
          </div>
          <button className="btn btn-outline" style={{ borderColor: '#f59e0b', color: '#f59e0b', fontSize: '0.82rem' }} disabled>
            Bientôt disponible
          </button>
        </div>
      )}

      {loading ? (
        <div className="grid-3">{[...Array(3)].map((_, i) => <div key={i} className="skeleton" style={{ height: 180 }} />)}</div>
      ) : companies.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon"><Building2 size={48} opacity={0.3} /></div>
          <h3>Aucune entreprise enregistrée</h3>
          {isInvestor && (
            <button className="btn btn-primary mt-3" onClick={() => { setShowModal(true); }}>
              <Plus size={16} /> Créer ma première entreprise
            </button>
          )}
        </div>
      ) : (
        <div className="grid-3">
          {companies.map(c => {
            const isActive = activeCompany?.id === c.id;
            return (
              <div key={c.id} className="card" style={{
                display: 'flex', flexDirection: 'column', gap: 10,
                border: isActive ? '2px solid var(--primary)' : '1px solid var(--border)',
                background: isActive ? 'var(--primary-glow)' : 'var(--bg-card)',
                position: 'relative',
              }}>
                {/* Active badge */}
                {isActive && (
                  <div style={{
                    position: 'absolute', top: 10, right: 10,
                    background: 'var(--primary)', color: 'white',
                    padding: '2px 8px', borderRadius: 12, fontSize: '0.7rem', fontWeight: 700,
                    display: 'flex', alignItems: 'center', gap: 4,
                  }}>
                    <Check size={10} /> Active
                  </div>
                )}

                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                  <div style={{
                    width: 44, height: 44, borderRadius: 'var(--radius)',
                    background: isActive ? 'var(--primary)' : 'rgba(20,184,166,0.12)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}>
                    <Building2 size={20} color={isActive ? 'white' : 'var(--primary)'} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: '1.05rem', color: 'var(--text-primary)' }}>{c.libelle}</div>
                    {c.domaine && <span className="badge badge-muted" style={{ marginTop: 4 }}>{c.domaine}</span>}
                  </div>
                </div>

                {c.description && (
                  <p style={{ fontSize: '0.875rem', color: 'var(--text-muted)', margin: 0, lineHeight: 1.4 }}>
                    {c.description}
                  </p>
                )}

                <div style={{ display: 'flex', gap: 12, fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                  {c.ville && <span>📍 {c.ville}{c.pays ? `, ${c.pays}` : ''}</span>}
                  {c.capacite && <span><Users size={12} style={{ display: 'inline' }} /> {c.capacite}</span>}
                </div>

                {/* Actions */}
                <div style={{ borderTop: '1px solid var(--border)', paddingTop: 10, display: 'flex', gap: 8, marginTop: 'auto' }}>
                  {!isActive && (
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={() => handleSwitch(c)}
                      style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
                    >
                      <Check size={13} /> Activer
                    </button>
                  )}
                  {isActive && (
                    <div style={{
                      flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                      padding: '6px 0', fontSize: '0.8rem', color: 'var(--primary)', fontWeight: 600
                    }}>
                      <Check size={13} /> Entreprise active
                    </div>
                  )}
                  {isInvestor && (
                    <>
                      <button className="btn btn-ghost btn-sm" onClick={() => { setEditCompany(c); setShowModal(true); }} title="Modifier">✏️</button>
                      <button className="btn btn-danger btn-sm" onClick={() => setDeleteId(c.id)} title="Supprimer"><Trash2 size={14} /></button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showModal && (
        <CompanyModal company={editCompany} onClose={() => { setShowModal(false); setEditCompany(null); }} onSave={handleSave} t={t} />
      )}

      <ConfirmModal
        isOpen={!!deleteId}
        title="Supprimer cette entreprise"
        message="Êtes-vous sûr de vouloir supprimer cette entreprise ? Les données associées resteront mais ne seront plus accessibles via ce profil."
        onConfirm={confirmDelete}
        onCancel={() => setDeleteId(null)}
      />
    </div>
  );
}
